import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

export interface BomComponent {
  productId: string;
  productName?: string;
  sku?: string;
  quantity: number;
  unit: string;
  unitCost: number;
  wastePercent?: number;
  notes?: string | null;
}

export interface Bom {
  id: string;
  productId: string;
  description: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'OBSOLETE';
  components: BomComponent[];
  totalCost: number;
  effectiveSince: string;
  effectiveUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BomInput {
  productId: string;
  description: string;
  effectiveSince?: string;
  components?: {
    productId: string;
    productName?: string;
    sku?: string;
    quantity: number;
    unit: string;
    unitCost: number;
    wastePercent?: number;
    notes?: string;
  }[];
}

export interface BomUpdateInput {
  id: string;
  description?: string;
  effectiveSince?: string;
  effectiveUntil?: string | null;
  components?: {
    productId: string;
    quantity: number;
    unit: string;
    unitCost: number;
    wastePercent?: number;
    notes?: string;
  }[];
  componentsToRemove?: string[];
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export function useBoms(params?: {
  productId?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<Bom>>> {
  return useQuery<PaginatedResponse<Bom>>({
    queryKey: [...queryKeys.manufacturing.boms(), params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<Bom>>('/manufacturing/boms', {
        params: {
          ...(params?.productId ? { productId: params.productId } : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.search ? { search: params.search } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

export function useBom(id: string): ReturnType<typeof useQuery<Bom>> {
  return useQuery<Bom>({
    queryKey: [...queryKeys.manufacturing.boms(), id],
    queryFn: async () => apiClient.get<Bom>(`/manufacturing/boms/${id}`),
    enabled: !!id,
  });
}

export function useCreateBom() {
  const queryClient = useQueryClient();

  return useMutation<{ bomId: string; version: number; totalCost: number; componentCount: number }, unknown, BomInput, unknown>({
    mutationFn: (input: BomInput) =>
      apiClient.post<{ bomId: string; version: number; totalCost: number; componentCount: number }>(
        '/manufacturing/boms',
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturing.boms() });
    },
  });
}

export function useUpdateBom() {
  const queryClient = useQueryClient();

  return useMutation<{ bomId: string; version: number; componentCount: number; totalCost: number }, unknown, BomUpdateInput, unknown>({
    mutationFn: (input: BomUpdateInput) => {
      const { id, ...body } = input;
      return apiClient.put<{ bomId: string; version: number; componentCount: number; totalCost: number }>(
        `/manufacturing/boms/${id}`,
        body,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturing.boms() });
    },
  });
}

export function useDeleteBom() {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.delete(`/manufacturing/boms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturing.boms() });
    },
  });
}

export function useActivateBom() {
  const queryClient = useQueryClient();

  return useMutation<{ bomId: string; version: number; status: string }, unknown, string, unknown>({
    mutationFn: (id: string) =>
      apiClient.post<{ bomId: string; version: number; status: string }>(
        `/manufacturing/boms/${id}/activate`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturing.boms() });
    },
  });
}
